export interface UserApiTenant {
  id: number;
  name: string;
  slug: string;
  is_active: number;
}

export interface UserApiUser {
  id: number;
  telegram_id: number;
  username: string | null;
  full_name: string | null;
  phone_number: string | null;
  is_blocked: boolean;
  created_at: string | null;
  tenant: UserApiTenant | null;
  wallet?: UserApiWallet;
  services?: UserApiService[];
  orders?: UserApiOrders;
}

export interface UserApiWallet {
  balance: number;
  transactions: UserApiTransaction[];
}

export interface UserApiTransaction {
  id: number;
  type: 'purchase' | 'topup' | 'admin_credit' | 'admin_debit';
  amount: number | null;
  status: string | null;
  method: string | null;
  title: string | null;
  created_at: string | null;
  reason?: string | null;
  balance_after?: number | null;
}

export interface UserApiService {
  id: number;
  name: string | null;
  status: 'active' | 'expired';
  expires_at: string | null;
}

export interface UserApiOrder {
  id: number;
  status: string | null;
  amount: number | null;
  payment_method: string | null;
  created_at: string | null;
  plan_name: string | null;
}

export interface UserApiOrders {
  total_count: number;
  latest: UserApiOrder | null;
}

export interface UserApiOrdersPage {
  items: UserApiOrder[];
  total_count: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface User {
  id: string;
  telegramId: number;
  name: string;
  username: string;
  phone: string;
  status: 'active' | 'suspended';
  tenantName: string;
  tenant: UserTenant | undefined;
  createdAt: string;
  wallet: UserWallet | undefined;
  services: UserService[] | undefined;
  orders: UserOrders | undefined;
}

export interface UserTenant {
  tenantId: string;
  tenantName: string;
  role?: string;
}

export interface UserService {
  id: string;
  name: string;
  status: 'active' | 'expired' | 'suspended';
  expiresAt: string;
}

export interface UserWalletTransaction {
  id: string;
  date: string;
  amount: number | null;
  type: 'credit' | 'debit';
  description: string;
  reason?: string;
  balanceAfter?: number;
}

export interface UserWallet {
  balance: number;
  transactions: UserWalletTransaction[];
}

export interface UserOrders {
  totalCount: number;
  latest: UserOrder | null;
}

export interface UserOrdersPage {
  items: UserOrder[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface UserOrder {
  id: string;
  status: string;
  amount: number | null;
  paymentMethod: string;
  createdAt: string;
  planName: string;
}

export function mapUserOrder(order: UserApiOrder): UserOrder {
  return {
    id: String(order.id),
    status: order.status || '-',
    amount: order.amount,
    paymentMethod: order.payment_method || '-',
    createdAt: order.created_at || '-',
    planName: order.plan_name || '-',
  };
}

export function mapUserOrdersPage(page: UserApiOrdersPage): UserOrdersPage {
  return {
    items: page.items.map(mapUserOrder),
    totalCount: page.total_count,
    page: page.page,
    pageSize: page.page_size,
    totalPages: page.total_pages,
  };
}

export function mapUser(user: UserApiUser): User {
  const tenant = user.tenant
    ? { tenantId: String(user.tenant.id), tenantName: user.tenant.name }
    : undefined;

  return {
    id: String(user.id),
    telegramId: user.telegram_id,
    name: user.full_name || user.username || String(user.telegram_id),
    username: user.username || '-',
    phone: user.phone_number || '-',
    status: user.is_blocked ? 'suspended' : 'active',
    tenantName: user.tenant?.name || 'بدون تننت',
    tenant,
    createdAt: user.created_at || '-',
    wallet: user.wallet ? {
      balance: user.wallet?.balance ?? 0,
      transactions: (user.wallet?.transactions || []).map((transaction) => ({
        id: String(transaction.id),
        date: transaction.created_at || '-',
        amount: transaction.amount,
        type: transaction.type === 'topup' || transaction.type === 'admin_credit' ? 'credit' : 'debit',
        description: transaction.title || '-',
        ...(transaction.reason ? { reason: transaction.reason } : {}),
        ...(transaction.balance_after == null ? {} : { balanceAfter: transaction.balance_after }),
      })),
    } : undefined,
    services: user.services?.map((service) => ({
      id: String(service.id),
      name: service.name || '-',
      status: service.status,
      expiresAt: service.expires_at || '-',
    })),
    orders: user.orders ? {
      totalCount: user.orders.total_count,
      latest: user.orders?.latest
        ? {
            id: String(user.orders.latest.id),
            status: user.orders.latest.status || '-',
            amount: user.orders.latest.amount,
            paymentMethod: user.orders.latest.payment_method || '-',
            createdAt: user.orders.latest.created_at || '-',
            planName: user.orders.latest.plan_name || '-',
          }
        : null,
    } : undefined,
  };
}
